import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { R2_IMAGES } from '../utils/r2Utils';

/**
 * 学术论文页面组件 - TVCG
 * 展示论文的详细信息，包括标题、作者、摘要和下载链接
 * 参照 r1-safety.github.io 的现代学术风格设计
 */
const AcademicPage_1_TVCG = () => {
  /**
   * 处理 Paper 按钮点击，打开论文链接
   */
  const handlePaperClick = () => {
    window.open(R2_IMAGES.tvcg25, '_blank');
  };

  return (
    <div style={{ 
      backgroundColor: '#ffffff', 
      minHeight: '100vh', 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' 
    }}>
      
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '60px 40px' }}>
        
        {/* 标题部分 */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ 
            fontSize: '38px', 
            fontWeight: '600', 
            color: '#1a1a1a', 
            lineHeight: '1.3',
            marginBottom: '20px',
            letterSpacing: '-0.02em'
          }}>
            Exploring and Modeling Gaze-Based Steering Behavior in Virtual Reality
          </h1>
          
          {/* 作者列表 */}
          <div style={{ 
            fontSize: '17px', 
            color: '#666', 
            lineHeight: '1.8',
            marginBottom: '12px'
          }}>
            Xuning Hu<sup>1</sup>, <span style={{ color: '#2563eb', fontWeight: '500' }}>Yichuan Zhang</span><sup>1</sup>, Yushi Wei<sup>1</sup>, Liangyuting Zhang<sup>1</sup>, Yue Li<sup>1</sup>, Wolfgang Stuerzlinger<sup>2</sup>, Hai-Ning Liang<sup>1*</sup>
          </div>
          
          {/* 机构信息 */}
          <div style={{ fontSize: '14px', color: '#888' }}>
            <sup>1</sup>The Hong Kong University of Science and Technology (Guangzhou)<br />
            <sup>2</sup>Simon Fraser University<br />
            <span style={{ fontSize: '15px', color: '#2563eb' }}>IEEE TVCG 2024</span>
          </div>
        </div>

        {/* 论文头图展示区域 */}
        <div style={{ 
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          <img 
            src={R2_IMAGES.tvcgLaser} 
            alt="Experimental Setup and Spatial Accuracy/Imprecision Simulation"
            style={{
              width: '100%',
              maxWidth: '800px'
            }}
          />
          <div style={{ 
            fontSize: '14px', 
            color: '#666', 
            lineHeight: '1.6', 
            marginTop: '16px',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
            fontStyle: 'italic',
            textAlign: 'justify'
          }}>
            The left side demonstrates our experimental setup, where participants complete a steering task using a gaze-based cursor
            in a VR environment. (a–b) illustrate simulated spatial inaccuracy by introducing a fixed offset between the user's actual gaze point
            and the displayed gaze cursor, representing systematic calibration errors (see Sec. 2.3 for formulas and details). (c–d) depict spatial
            imprecision, modeled by adding Gaussian noise to the gaze cursor position to reflect reduced stability in gaze estimation (see Sec. 2.3
            for formulas and details).
          </div>
        </div>

        {/* 按钮部分 */}
        <div style={{ 
          marginBottom: '50px',
          textAlign: 'center'
        }}>
          <button 
            onClick={handlePaperClick}
            style={{
              padding: '12px 28px',
              fontSize: '15px',
              fontWeight: '500',
              color: '#ffffff',
              backgroundColor: '#374151',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#4b5563';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#374151';
            }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            Paper
          </button>
        </div>

        {/* 摘要部分 */}
        <div style={{ marginBottom: '50px' }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1a1a1a', 
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Abstract
          </h2>
          
          <div style={{ 
            fontSize: '16px', 
            color: '#2d2d2d', 
            lineHeight: '1.7', 
            textAlign: 'justify',
            maxWidth: '780px',
            margin: '0 auto'
          }}>
            Recent advances in eye-tracking technology have positioned gaze as an efficient and intuitive input method for Virtual Reality (VR), 
            offering a natural and immersive user experience. As a result, gaze input is now leveraged for fundamental interaction tasks such as 
            selection, manipulation, crossing, and steering. Although several studies have modeled user steering performance across various path 
            characteristics and input methods, our understanding of gaze-based steering in VR remains limited. This gap persists because the unique 
            qualities of eye movements—involving rapid, continuous motions—and the variability in eye-tracking make findings from other input 
            modalities nontransferable to a gaze-based context, underscoring the need for a dedicated investigation into gaze-based steering behaviors 
            and performance. To bridge this gap, we present two user studies to explore and model gaze-based steering. In the first one, user behavior 
            data are collected across various path characteristics and eye-tracking conditions. Based on this data, we propose four refined models that 
            extend the classic Steering Law to predict users' movement time in gaze-based steering tasks, explicitly incorporating the impact of tracking 
            quality. The best-performing model achieves an adjusted R² of 0.956, corresponding to a 16% improvement in movement time prediction. This 
            model also yields a substantial reduction in AIC (from 1550 to 1132) and BIC (from 1555 to 1142), highlighting improved model quality and 
            better balance between goodness of fit and model complexity. Finally, data from a second study with varied settings, such as a different 
            eye-tracking sampling rate, illustrate the strong robustness and predictability of our models. Finally, we present scenarios and applications 
            that demonstrate how our models can be used to design enhanced gaze-based interactions in VR systems.
          </div>
        </div>

        {/* 研究问题部分 */}
        <div style={{ marginBottom: '50px' }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1a1a1a', 
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Research Questions
          </h2>
          
          <div style={{ 
            maxWidth: '820px',
            margin: '0 auto'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '18px',
              gap: '12px'
            }}>
              <div style={{
                flexShrink: 0,
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                💡
              </div>
              <div style={{ 
                fontSize: '15.5px', 
                color: '#2d2d2d', 
                lineHeight: '1.6',
                flex: 1,
                textAlign: 'left'
              }}>
                <strong style={{ color: '#2563eb' }}>RQ1:</strong> How do path features, such as width and length, influence user
                behavior in gaze-based steering tasks?
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '18px',
              gap: '12px'
            }}>
              <div style={{
                flexShrink: 0,
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                💡
              </div>
              <div style={{ 
                fontSize: '15.5px', 
                color: '#2d2d2d', 
                lineHeight: '1.6',
                flex: 1,
                textAlign: 'left'
              }}>
                <strong style={{ color: '#2563eb' }}>RQ2:</strong> How do spatial precision and accuracy of eye tracking influence user
                behavior in gaze-based steering tasks?
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '18px',
              gap: '12px'
            }}>
              <div style={{
                flexShrink: 0,
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                💡
              </div>
              <div style={{ 
                fontSize: '15.5px', 
                color: '#2d2d2d', 
                lineHeight: '1.6',
                flex: 1,
                textAlign: 'left'
              }}>
                <strong style={{ color: '#2563eb' }}>RQ3:</strong> How does user behavior in gaze-based cursor control align
                with the Steering Law?
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                flexShrink: 0,
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                💡
              </div>
              <div style={{ 
                fontSize: '15.5px', 
                color: '#2d2d2d', 
                lineHeight: '1.6',
                flex: 1,
                textAlign: 'left'
              }}>
                <strong style={{ color: '#2563eb' }}>RQ4:</strong> Can the effects of spatial accuracy and precision on user
                behavior in steering tasks be captured or explained through mathematical modeling?
              </div>
            </div>
          </div>
        </div>

        {/* 公式说明部分 */}
        <div style={{ marginBottom: '50px' }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1a1a1a', 
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Methods: Spatial Accuracy and Imprecision
          </h2>

          <div style={{ 
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {/* Accuracy 公式 */}
            <div style={{ 
              marginBottom: '40px'
            }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '16px' 
              }}>
                Accuracy
              </div>
              <div style={{ 
                textAlign: 'center',
                marginBottom: '16px',
                padding: '20px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <BlockMath math="\text{Accuracy} = \frac{1}{N}\sum_{i=1}^{N} \arccos\!\Biggl(\frac{g_i \cdot t_i}{\|g_i\|\;\|t_i\|}\Biggr)" />
              </div>
              <div style={{ 
                fontSize: '15px', 
                color: '#4b5563', 
                lineHeight: '1.7',
                textAlign: 'justify'
              }}>
                This formula calculates the <strong>average angular difference</strong> between ground truth vectors (g<sub>i</sub>) and test/predicted vectors (t<sub>i</sub>). 
                The term arccos((g<sub>i</sub> ⋅ t<sub>i</sub>) / (||g<sub>i</sub>|| ⋅ ||t<sub>i</sub>||)) computes the angle (in radians) between 
                corresponding vectors for each sample i, where N is the total number of samples. A lower Accuracy value indicates better alignment between 
                the ground truth and measured gaze directions.
              </div>
            </div>

            {/* Precision 公式 */}
            <div style={{ 
              marginBottom: '40px'
            }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '16px' 
              }}>
                Precision
              </div>
              <div style={{ 
                textAlign: 'center',
                marginBottom: '16px',
                padding: '20px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <BlockMath math="\text{Precision} = \sqrt{\frac{1}{N-1}\sum_{i=1}^{N}\|g_i - \bar{g}\|^2}" />
              </div>
              <div style={{ 
                fontSize: '15px', 
                color: '#4b5563', 
                lineHeight: '1.7',
                textAlign: 'justify'
              }}>
                This formula measures the <strong>standard deviation</strong> or spread of gaze measurements. It calculates the square root of the average 
                squared distance between each data point g<sub>i</sub> and the mean ḡ across all measurements. The division by (N-1) provides an 
                unbiased estimate of the variance, making this a standard measure of measurement stability and precision in eye-tracking systems.
              </div>
            </div>

            {/* Spatial Imprecision 公式 - Gaussian Noise */}
            <div style={{ 
              marginBottom: '40px'
            }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '16px' 
              }}>
                Spatial Imprecision: Gaussian Noise
              </div>
              <div style={{ 
                textAlign: 'center',
                marginBottom: '16px',
                padding: '20px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <BlockMath math="\mathbf{P}_{\mathrm{degraded}} = \mathbf{P}_{\mathrm{original}} + \begin{pmatrix} \epsilon_x \\ \epsilon_y \\ 0 \end{pmatrix}, \quad \epsilon_x,\, \epsilon_y \sim \mathcal{N}(0, \sigma^2)" />
              </div>
              <div style={{ 
                fontSize: '15px', 
                color: '#4b5563', 
                lineHeight: '1.7',
                textAlign: 'justify'
              }}>
                This formulation adds <strong>Gaussian noise</strong> to the original gaze position to simulate spatial imprecision. The degraded position 
                (P<sub>degraded</sub>) is obtained by adding a noise vector with random components ε<sub>x</sub> and ε<sub>y</sub> drawn from a normal 
                distribution with zero mean and variance σ². This models the variability in gaze estimation stability, where smaller σ² values indicate 
                higher precision and more stable eye-tracking.
              </div>
            </div>

            {/* Spatial Inaccuracy 公式 - Fixed Offset */}
            <div style={{ 
              marginBottom: '40px'
            }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '16px' 
              }}>
                Spatial Inaccuracy: Fixed Offset
              </div>
              <div style={{ 
                textAlign: 'center',
                marginBottom: '16px',
                padding: '20px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <BlockMath math="P_{\mathrm{degraded}} = P_{\mathrm{original}} + \mathrm{Vec3}\bigl(\cos(\theta), \sin(\theta), 0\bigr) \times \mathrm{Mag}" />
              </div>
              <div style={{ 
                fontSize: '15px', 
                color: '#4b5563', 
                lineHeight: '1.7',
                textAlign: 'justify'
              }}>
                This equation introduces a <strong>systematic offset</strong> to simulate spatial inaccuracy in eye-tracking calibration. The degraded 
                position is shifted from the original by a fixed vector with magnitude Mag and direction defined by angle θ in the XY-plane. 
                The vector has no Z-component, keeping the offset in 2D space. This represents calibration errors where the gaze cursor consistently 
                deviates from the true gaze point in a specific direction.
              </div>
            </div>
          </div>
        </div>

        {/* 图片展示区域 */}
        <div style={{ 
          marginBottom: '50px',
          textAlign: 'center'
        }}>
          <div style={{ 
            fontSize: '16px',
            fontWeight: '600',
            color: '#1a1a1a',
            marginBottom: '20px'
          }}>
            Experimental SetUp
          </div>
          <img 
            src={R2_IMAGES.taskDesign} 
            alt="Experimental Setup and Task Design"
            style={{
              width: '100%',
              maxWidth: '900px'
            }}
          />
        </div>

        {/* Experimental Design and Parameters */}
        <div style={{ 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1a1a1a', 
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Experimental Design and Parameters
          </h2>

          <div style={{ 
            fontSize: '16px', 
            color: '#2d2d2d', 
            lineHeight: '1.7', 
            textAlign: 'justify',
            marginBottom: '24px'
          }}>
            The user study employed a <strong>2 × 3 × 4 × 4 within-subjects design</strong> with four independent variables, resulting in <strong>96 experimental conditions</strong> in total:
          </div>

          <ul style={{
            listStyle: 'none',
            padding: 0,
            marginBottom: '30px'
          }}>
            <li style={{
              marginBottom: '16px',
              fontSize: '16px',
              color: '#2d2d2d',
              lineHeight: '1.7'
            }}>
              • <strong>Path Length (A):</strong> 30° and 50°
            </li>
            <li style={{
              marginBottom: '16px',
              fontSize: '16px',
              color: '#2d2d2d',
              lineHeight: '1.7'
            }}>
              • <strong>Path Width (W):</strong> 3°, 4.5°, and 6°
            </li>
            <li style={{
              marginBottom: '16px',
              fontSize: '16px',
              color: '#2d2d2d',
              lineHeight: '1.7'
            }}>
              • <strong>Artificial Spatial Accuracy (Ac):</strong> 0°, 1°, 2°, and 3°
            </li>
            <li style={{
              fontSize: '16px',
              color: '#2d2d2d',
              lineHeight: '1.7'
            }}>
              • <strong>Artificial Spatial Precision (Pr):</strong> 0°, 0.5°, 1°, and 1.5°
            </li>
          </ul>

          <div style={{ 
            fontSize: '15px', 
            color: '#4b5563', 
            lineHeight: '1.7',
            textAlign: 'justify'
          }}>
            <p style={{ marginBottom: '16px' }}>
              <strong>Path length</strong> was defined as the distance from the starting region to the ending region, while <strong>path width</strong> referred to the diameter of the target ball (see Figure 2 above). The design of path width and path length followed the difficulty index (ID<sub>s</sub>) from the original Steering Law (Equation 3), ranging from <strong>5.0 to 16.67 bits</strong>, covering a spectrum from easy to difficult task conditions.
            </p>
            <p>
              The <strong>spatial accuracy</strong> and <strong>spatial precision</strong> parameters correspond to the artificial degradation conditions discussed earlier (see Spatial Accuracy and Imprecision formulas above). These parameters controlled the systematic offset and random noise applied to the gaze cursor, simulating various tracking quality conditions in eye-tracking systems.
            </p>
          </div>
        </div>

        {/* Experimental Results */}
        <div style={{ 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1a1a1a', 
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Experimental Results
          </h2>

          <div style={{ 
            marginBottom: '50px',
            textAlign: 'center'
          }}>
            <img 
              src={R2_IMAGES.allObjectiveData}
              alt="Experimental Results"
              style={{
                width: '100%',
                maxWidth: '900px'
              }}
            />
          </div>
        </div>

        {/* Proposed Model & Fitting */}
        <div style={{ 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1a1a1a', 
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Proposed Model & Fitting
          </h2>

          {/* First Table: Ac and Pr fitting comparison - placed before text */}
          <div style={{
            overflowX: 'auto',
            marginBottom: '40px'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              <thead>
                <tr style={{
                  borderBottom: '2px solid #333',
                  padding: '12px'
                }}>
                  <th style={{ 
                    padding: '12px', 
                    fontWeight: '600',
                    textAlign: 'left'
                  }}>Factor</th>
                  <th style={{ padding: '12px', fontWeight: '600' }}>Model</th>
                  <th style={{ padding: '12px', fontWeight: '600' }}>Adjusted R²</th>
                  <th style={{ padding: '12px', fontWeight: '600' }}>AIC</th>
                  <th style={{ padding: '12px', fontWeight: '600' }}>BIC</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderTop: '1px solid #ddd' }}>
                  <td rowSpan="4" style={{ 
                    padding: '12px',
                    fontWeight: '600',
                    verticalAlign: 'middle',
                    textAlign: 'center'
                  }}>Ac</td>
                  <td style={{ padding: '12px' }}>
                    <InlineMath math="MT = a + b \cdot Ac" />
                  </td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>0.975</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>37.214</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>35.986</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px' }}>
                    <InlineMath math="MT = a + b \cdot Ac^c" />
                  </td>
                  <td style={{ padding: '12px' }}>0.902</td>
                  <td style={{ padding: '12px' }}>42.686</td>
                  <td style={{ padding: '12px' }}>41.45</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px' }}>
                    <InlineMath math="MT = a + b \cdot \ln(Ac)" />
                  </td>
                  <td style={{ padding: '12px' }}>0.877</td>
                  <td style={{ padding: '12px' }}>43.580</td>
                  <td style={{ padding: '12px' }}>42.353</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '12px' }}>
                    <InlineMath math="MT = a + b \cdot e^{Ac}" />
                  </td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>0.980</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>36.189</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>34.962</td>
                </tr>
                <tr style={{ borderTop: '1px solid #ddd' }}>
                  <td rowSpan="4" style={{ 
                    padding: '12px',
                    fontWeight: '600',
                    verticalAlign: 'middle',
                    textAlign: 'center'
                  }}>Pr</td>
                  <td style={{ padding: '12px' }}>
                    <InlineMath math="MT = a + b \cdot Pr" />
                  </td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>0.987</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>36.911</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>35.684</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px' }}>
                    <InlineMath math="MT = a + b \cdot Pr^c" />
                  </td>
                  <td style={{ padding: '12px' }}>0.931</td>
                  <td style={{ padding: '12px' }}>43.766</td>
                  <td style={{ padding: '12px' }}>42.539</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px' }}>
                    <InlineMath math="MT = a + b \cdot \ln(Pr)" />
                  </td>
                  <td style={{ padding: '12px' }}>0.899</td>
                  <td style={{ padding: '12px' }}>45.264</td>
                  <td style={{ padding: '12px' }}>44.036</td>
                </tr>
                <tr style={{ borderBottom: '2px solid #333' }}>
                  <td style={{ padding: '12px' }}>
                    <InlineMath math="MT = a + b \cdot e^{Pr}" />
                  </td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>0.992</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>35.042</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>33.815</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ 
            fontSize: '16px', 
            color: '#2d2d2d', 
            lineHeight: '1.7', 
            textAlign: 'justify',
            marginBottom: '30px'
          }}>
            <p style={{ marginBottom: '16px' }}>
              Given the absence of prior work specifically addressing gaze-based steering tasks, we first adopted Zhang et al.'s extension of Fitts' Law (Eq. (4)) [62], which integrates gaze-specific characteristics for selection tasks, as our <strong>Candidate Model 1 (CM1)</strong>. To determine whether spatial accuracy and precision should be incorporated into predictive steering models, we computed generalized eta-squared (η<sup>2</sup><sub>G</sub>) values to quantify their contributions to movement time variance. The results showed substantial effect sizes for amplitude (A, η<sup>2</sup><sub>G</sub> = 0.372), width (W, η<sup>2</sup><sub>G</sub> = 0.512), accuracy (Ac, η<sup>2</sup><sub>G</sub> = 0.108), precision (Pr, η<sup>2</sup><sub>G</sub> = 0.176), and the interaction between amplitude and width (A × W, η<sup>2</sup><sub>G</sub> = 0.076). Following Cohen's criteria for effect sizes [9] (small: 0.01, medium: 0.06, large: 0.14), only factors and interactions with medium or larger effects were considered further. Our findings clearly indicate that both spatial accuracy and precision significantly influence steering performance without notable interaction effects. Thus, incorporating these two gaze-tracking quality factors into the predictive models is warranted.
            </p>
            <p style={{ marginBottom: '16px' }}>
              Based on these quantified relationships (see Section Sec. 5.2), we extended the original Steering Law (Eq. (3)) by linearly integrating spatial accuracy to create <strong>Candidate Model 2 (CM2)</strong>. Similarly, spatial precision was incorporated into the original model separately, resulting in <strong>Candidate Model 3 (CM3)</strong>. Finally, both spatial accuracy and precision were simultaneously integrated into the original model to develop <strong>Candidate Model 4 (CM4)</strong>. The detailed derivation and structure of these candidate models are summarized in Sec. 5.4.2.
            </p>
          </div>

          <div style={{ 
            marginBottom: '40px',
            textAlign: 'center'
          }}>
            <img 
              src={R2_IMAGES.candidateModelFitting}
              alt="Model Fitting Results"
              style={{
                width: '100%',
                maxWidth: '900px'
              }}
            />
          </div>

          {/* Second Table: 5 model comparison - after image */}
          <div style={{
            overflowX: 'auto',
            marginBottom: '20px'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              <thead>
                <tr style={{
                  borderBottom: '2px solid #333',
                  padding: '12px'
                }}>
                  <th style={{ 
                    padding: '12px', 
                    fontWeight: '600',
                    textAlign: 'left'
                  }}>Model</th>
                  <th style={{ padding: '12px', fontWeight: '600' }}>R²</th>
                  <th style={{ padding: '12px', fontWeight: '600' }}>AIC</th>
                  <th style={{ padding: '12px', fontWeight: '600' }}>BIC</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderTop: '1px solid #ddd' }}>
                  <td style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>
                    BL: <InlineMath math="MT = a + b \cdot \frac{A}{W}" />
                  </td>
                  <td style={{ padding: '12px' }}>0.795</td>
                  <td style={{ padding: '12px' }}>1550</td>
                  <td style={{ padding: '12px' }}>1555</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>
                    CM1: <InlineMath math="MT = a + b \cdot \frac{e^{\lambda A}}{W - \mu}" />
                  </td>
                  <td style={{ padding: '12px' }}>0.796</td>
                  <td style={{ padding: '12px' }}>1278</td>
                  <td style={{ padding: '12px' }}>1288</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px', textAlign: 'left' }}>
                    CM2: <InlineMath math="MT = a + b \frac{A}{W} + c \cdot Ac" />
                  </td>
                  <td style={{ padding: '12px' }}>0.849</td>
                  <td style={{ padding: '12px' }}>1248</td>
                  <td style={{ padding: '12px' }}>1256</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px', textAlign: 'left' }}>
                    CM3: <InlineMath math="MT = a + b \frac{A}{W} + c \cdot Pr" />
                  </td>
                  <td style={{ padding: '12px' }}>0.898</td>
                  <td style={{ padding: '12px' }}>1211</td>
                  <td style={{ padding: '12px' }}>1218</td>
                </tr>
                <tr style={{ borderBottom: '2px solid #333' }}>
                  <td style={{ padding: '12px', textAlign: 'left' }}>
                    CM4: <InlineMath math="MT = a + b \frac{A}{W} + c \cdot Ac + d \cdot Pr" />
                  </td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>0.956</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>1132</td>
                  <td style={{ 
                    padding: '12px',
                    backgroundColor: '#fef08a',
                    fontWeight: '600'
                  }}>1142</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 应用场景部分 */}
        <div style={{ 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1a1a1a', 
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Application Scenarios
          </h2>

          {/* 应用场景图片 */}
          <div style={{ 
            marginBottom: '30px',
            textAlign: 'center'
          }}>
            <img 
              src={R2_IMAGES.tvcgApplication} 
              alt="Application Scenarios for Gaze-Based Steering"
              style={{
                width: '100%',
                maxWidth: '900px'
              }}
            />
            <div style={{ 
              fontSize: '14px', 
              color: '#666', 
              lineHeight: '1.6', 
              marginTop: '16px',
              maxWidth: '900px',
              marginLeft: 'auto',
              marginRight: 'auto',
              fontStyle: 'italic',
              textAlign: 'justify'
            }}>
              Application scenarios for gaze-based steering interaction, where (a) illustrates a gaze-controlled cascading menu 
              utilizing steering behavior for item selection, (b) a notification dismissal mechanism, where users steer their gaze 
              along a narrow path to remove pop-ups without manual interaction, (c) presents a scenario on Apple Vision Pro where 
              a phone call is answered using gaze steering.
            </div>
          </div>

          {/* 应用场景说明文字 */}
          <div style={{ 
            fontSize: '16px', 
            color: '#2d2d2d', 
            lineHeight: '1.7', 
            textAlign: 'justify',
            marginBottom: '24px'
          }}>
            <p style={{ marginBottom: '16px' }}>
              We present three example application scenarios that demonstrate the practical utility of our gaze-based steering models. 
              As illustrated in Fig. 6a, a user interacts with a cascading menu using a gaze cursor in VR. Under conditions with lower 
              device eye-tracking quality, Candidate Model 4 can be used to adjust the menu's width and height to optimize the user 
              experience. Another example is shown in Fig. 6b, where the user avoids interruptive pop-up notifications through gaze 
              steering. When a message or alert appears, the user can steer their gaze along a narrow path to slide the notification 
              up and out of view. Our model can help adjust the width of the control bars based on variations in spatial accuracy 
              and precision across different user groups or eye-tracking systems. Finally, as gaze interaction is the primary input 
              for the Apple Vision Pro, Fig. 6c shows an example scenario where the user answers a phone call via gaze-steering—steering 
              left to decline or right to accept the call.
            </p>
            <p>
              As one of the most intuitive forms of interaction in VR, gaze-based interaction offers unique advantages. We believe 
              that our exploration of spatial accuracy and precision can assist designers in adjusting path parameters based on the 
              performance characteristics of different eye-tracking systems. For instance, designers may personalize adaptive gaze-steering 
              tasks for individual users based on spatial accuracy and precision metrics obtained during eye-tracker calibration. 
              When eye-tracking quality is suboptimal, our proposed formula can help balance task difficulty across users in gaze 
              steering scenarios, ensuring consistent user performance and experience. This approach ultimately mitigates negative user 
              experiences caused by eye-tracking limitations across HMDs.
            </p>
          </div>
        </div>

        {/* 引用信息 */}
        <div style={{
          backgroundColor: '#f9fafb',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          fontSize: '14px',
          color: '#4b5563',
          fontFamily: 'Consolas, "Courier New", monospace'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: '600', color: '#1f2937' }}>BibTeX:</div>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`@article{hu2024exploring,
  title={Exploring and Modeling Gaze-Based Steering Behavior in Virtual Reality},
  author={Hu, Xuning and Zhang, Yichuan and Wei, Yushi and Zhang, Liangyuting and Li, Yue and Stuerzlinger, Wolfgang and Liang, Hai-Ning},
  journal={IEEE Transactions on Visualization and Computer Graphics},
  year={2024}
}`}
          </pre>
        </div>

      </div>
    </div>
  );
};

export default AcademicPage_1_TVCG;
